import { apiClient, clean } from "./apiClient";
import type { PageRequest, PageResponse } from "@/types/api";
import type { ContactDetails } from "@/types/contact";
import type { ContactCreatePayload } from "@/types/contactCreate";
import type { ContactDeletionImpact, ContactRef, ContactTransfer } from "@/types/contactDeletion";
import { contactCreateResponseToSummary } from "@/modules/contacts/contactFormUtils";
import type { ContactFilter, ContactSummary, ContactFilterOptions, Option } from "@/types/entities";
import {
  buildContactFileNotePayload,
  noteEntryToPreserve,
  notesService,
  resolveNoteBranchIds,
} from "./notesService";
import { usersService } from "./usersService";
import type { ContactNoteEntry } from "@/types/contact";
import { normalizeContactPropertyListFilter } from "@/lib/contactPropertyListScope";
import {
  normalizeContactFilterOptions,
  normalizeProfileIntentionFilter,
} from "@/lib/contactFilterUtils";
import { useSettingsStore } from "@/store/settingsStore";

function contactListParams(filter: ContactFilter, page: PageRequest) {
  const normalized = normalizeContactPropertyListFilter(filter);
  const { archived, isActiveProfile, profileIntention, labelIds, ...rest } = normalized;
  const params = { ...clean(rest), ...clean(page) };

  const intention = normalizeProfileIntentionFilter(profileIntention);
  if (intention) params.profileIntention = intention;

  const ids = (labelIds ?? []).map((id) => String(id).trim()).filter(Boolean);
  if (ids.length > 0) params.labelIds = ids;

  // Explicit booleans — archived=false / isActiveProfile=false must reach the API.
  if (archived === true || archived === false) params.archived = archived;
  if (isActiveProfile === true || isActiveProfile === false) {
    params.isActiveProfile = isActiveProfile;
  }

  return params;
}

/**
 * `/contacts/by-labels` is served from atlas-api's platform base (`/api/v1`),
 * not the UI base (`/api/ui/v1`) the rest of this file uses. The base is
 * overridden per request rather than by a second axios instance so the shared
 * interceptors still attach the bearer token and normalise the error envelope.
 */
function platformBaseUrl(): string {
  const uiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  // Same-origin default: `/api` is proxied to atlas-api in dev and claimed by
  // the load balancer in deployments, exactly as `/api/ui/v1` is.
  if (!uiBase) return "/api/v1";
  if (/\/ui\/v1\/?$/.test(uiBase)) return uiBase.replace(/\/ui\/v1\/?$/, "/v1");
  try {
    return `${new URL(uiBase).origin}/api/v1`;
  } catch {
    return "/api/v1";
  }
}

/** The endpoint speaks snake_case, unlike the UI API. */
interface ByLabelsRequest {
  label_ids: string[];
  target_type: string;
}

/**
 * Contact ids out of a `/contacts/by-labels` response.
 *
 * It answers `{ "contact_ids": [...] }` — ids only, no contact records — so the
 * names still have to come from the contacts API. A bare array and rows of
 * objects are accepted too, since the shape is not pinned down by a contract.
 */
function byLabelsContactIds(data: unknown): string[] {
  const rows = (() => {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== "object") return [];
    const envelope = data as Record<string, unknown>;
    for (const key of ["contact_ids", "contactIds", "contacts", "data", "items", "results"]) {
      if (Array.isArray(envelope[key])) return envelope[key];
    }
    return [];
  })();

  const ids: string[] = [];
  for (const row of rows) {
    const raw =
      typeof row === "string" || typeof row === "number"
        ? row
        : ((row as Record<string, unknown>)?.id ??
          (row as Record<string, unknown>)?.contact_id ??
          (row as Record<string, unknown>)?.contactId);
    if (raw == null || raw === "") continue;
    ids.push(String(raw));
  }
  // The same contact can sit under several of the labels asked about.
  return [...new Set(ids)];
}

export const contactsService = {
  list(
    filter: ContactFilter,
    page: PageRequest,
    options?: { signal?: AbortSignal },
  ): Promise<PageResponse<ContactSummary>> {
    return apiClient
      .get<PageResponse<ContactSummary>>("/contacts", {
        params: contactListParams(filter, page),
        // `labelIds` has to repeat the bare key — `labelIds=a&labelIds=b` —
        // where axios would otherwise bracket it as `labelIds[]=a`.
        paramsSerializer: { indexes: null },
        signal: options?.signal,
      })
      .then((r) => r.data);
  },

  getFilterOptions(): Promise<ContactFilterOptions> {
    return apiClient
      .get<ContactFilterOptions>("/contacts/options")
      .then((r) => normalizeContactFilterOptions(r.data));
  },

  getFurnishings(): Promise<Option[]> {
    return apiClient.get<Option[]>("/contacts/furnishings").then((r) => r.data);
  },

  getPositionTypes(): Promise<Option[]> {
    return apiClient.get<Option[]>("/contacts/position-types").then((r) => r.data);
  },

  create(body: ContactCreatePayload): Promise<ContactSummary> {
    return apiClient
      .post<ContactDetails>("/contacts", body)
      .then((r) => contactCreateResponseToSummary(r.data, body));
  },  async getOne(id: string, legalEntityId?: string): Promise<ContactDetails> {
    const response = await apiClient.get<ContactDetails>(`/contacts/${id}`, {
      params: { legalEntityId },
    });
    return response.data;
  },  update: async (id: string, body: Record<string, unknown>): Promise<ContactDetails> => {
    const current = await contactsService.getOne(id);
    const merged = { ...current, ...body } as ContactDetails & { contactNotes?: unknown };
    delete merged.contactNotes;
    delete merged.crmSyncStatus;
    delete (merged as any).syncAlto;
    await apiClient.patch<ContactDetails>(`/contacts/${id}`, merged);
    return contactsService.getOne(id);
  },

  /**
   * Create a file note via POST /notes (linked to the contact), then reload GET contact
   * for `contactNotes`. PATCH /contacts does not persist `contactNotes`.
   */
  async addContactFileNote(
    contactId: string,
    note: { title: string; notes: string },
  ): Promise<ContactDetails> {
    const contact = await contactsService.getOne(contactId);
    const sourceCrm = contact.sourceCrm?.trim();
    if (!sourceCrm) {
      throw new Error("Contact sourceCrm is required to create a file note");
    }
    const legalEntityId = contact.legalEntityId?.trim();
    if (!legalEntityId) {
      throw new Error("Contact legalEntityId is required to create a file note");
    }

    const author = await usersService.resolveCurrentUserAuthor();

    const payload = buildContactFileNotePayload({
      sourceCrm,
      legalEntityId,
      title: note.title,
      notes: note.notes,
      contactId,
      contactExternalId: contact.externalId ?? null,
      externalAuthorId: author.externalAuthorId,
      externalAuthorName: author.externalAuthorName,
      branchId: resolveNoteBranchIds(useSettingsStore.getState().branchId, contact.branchId)?.[0] ?? null,
    });

    await notesService.create(payload);

    return contactsService.getOne(contactId);
  },

  /**
   * Update a file note via PATCH /notes/{id} (keeps contact link), then GET contact
   * for refreshed `contactNotes`.
   */
  async updateContactFileNote(
    contactId: string,
    noteId: string,
    note: { title: string; notes: string },
    existingNote: ContactNoteEntry,
  ): Promise<ContactDetails> {
    const contact = await contactsService.getOne(contactId);
    const sourceCrm = (existingNote.sourceCrm ?? contact.sourceCrm)?.trim();
    const legalEntityId = (existingNote.legalEntityId ?? contact.legalEntityId)?.trim();
    if (!sourceCrm) {
      throw new Error("sourceCrm is required to update a file note");
    }
    if (!legalEntityId) {
      throw new Error("legalEntityId is required to update a file note");
    }
    const noteExternalId = existingNote.externalId?.trim() || existingNote.id?.trim() || null;
    if (!noteExternalId) {
      throw new Error("Note externalId is required to update a file note");
    }

    const payload = buildContactFileNotePayload({
      sourceCrm,
      legalEntityId,
      title: note.title,
      notes: note.notes,
      contactId,
      contactExternalId: contact.externalId ?? null,
      branchId:
        resolveNoteBranchIds(useSettingsStore.getState().branchId, contact.branchId)?.[0] ??
        (Array.isArray(existingNote.branchId)
          ? existingNote.branchId[0]
          : typeof existingNote.branchId === "string"
            ? existingNote.branchId
            : null),
      preserve: noteEntryToPreserve({ ...existingNote, externalId: noteExternalId }),
    });

    console.log("Editing note", existingNote);
    await notesService.update(noteId, payload);

    return contactsService.getOne(contactId);
  },

  /** Archives a contact (sets `archived`). Everything stays in the database. */
  remove(id: string) {
    return apiClient.delete(`/contacts/${id}`);
  },

  /**
   * Dry run for {@link deletePermanently} — reports every row and knowledge-graph
   * node the delete would remove. Changes nothing, so it is safe to call as soon
   * as the operator asks to delete.
   */
  previewDeletion(ids: string[]): Promise<ContactDeletionImpact> {
    return apiClient
      .post<ContactDeletionImpact>("/contacts/deletion/preview", { contactIds: ids })
      .then((r) => r.data);
  },

  /**
   * Permanently deletes contacts, their child records and their memory. Not
   * recoverable — only call this after the operator has confirmed a preview.
   * Takes an array so single and bulk deletes share one path.
   */
  deletePermanently(ids: string[]): Promise<ContactDeletionImpact> {
    return apiClient
      .post<ContactDeletionImpact>("/contacts/deletion", { contactIds: ids })
      .then((r) => r.data);
  },

  /**
   * Reads a contact's failed CRM sync. When it is a 409 duplicate, reports the CRM id
   * it points at and the Atlas contact holding that id.
   */
  getDuplicateConflict(id: string): Promise<ContactTransfer> {
    return apiClient.get<ContactTransfer>(`/contacts/${id}/duplicate-conflict`).then((r) => r.data);
  },

  /** Contacts this one's history can be transferred onto — by CRM id or name. */
  findTransferCandidates(id: string, q?: string): Promise<ContactRef[]> {
    return apiClient
      .get<ContactRef[]>(`/contacts/${id}/transfer-candidates`, { params: clean({ q }) })
      .then((r) => r.data);
  },

  /** Dry run for {@link transferHistory} — changes nothing. */
  previewTransfer(id: string, targetExternalId: string): Promise<ContactTransfer> {
    return apiClient
      .post<ContactTransfer>(`/contacts/${id}/transfer/preview`, null, { params: { targetExternalId } })
      .then((r) => r.data);
  },

  /** Moves notes and diary entries onto the target, then republishes each of them. */
  transferHistory(id: string, targetExternalId: string): Promise<ContactTransfer> {
    return apiClient
      .post<ContactTransfer>(`/contacts/${id}/transfer`, null, { params: { targetExternalId } })
      .then((r) => r.data);
  },

  /** Deletes a persisted phone number row. Only ever called for rows that carry an id. */
  removePhoneNumber(contactId: string, phoneId: string) {
    return apiClient.delete(`/contacts/${contactId}/phone-numbers/${phoneId}`);
  },

  /** Deletes a persisted email address row. Only ever called for rows that carry an id. */
  removeEmailAddress(contactId: string, emailId: string) {
    return apiClient.delete(`/contacts/${contactId}/email-addresses/${emailId}`);
  },

  /**
   * Ids of the contacts grouped by the given labels.
   *
   * The label listing only carries a count (and not always the ids), so the
   * membership shown when a label is opened is asked for here. The endpoint
   * answers with ids alone — the caller names them from contacts it already
   * holds, or with `getOne`.
   */
  async listContactIdsByLabels(
    labelIds: string[],
    options?: { targetType?: string; signal?: AbortSignal },
  ): Promise<string[]> {
    const ids = labelIds.filter(Boolean).map(String);
    if (ids.length === 0) return [];

    const body: ByLabelsRequest = {
      label_ids: ids,
      target_type: options?.targetType ?? "whatsapp",
    };
    const response = await apiClient.post("/contacts/by-labels", body, {
      baseURL: platformBaseUrl(),
      signal: options?.signal,
    });
    return byLabelsContactIds(response.data);
  },

};
